import { workOrderService } from './workOrderService'
import { workOrderOfflineStorage } from './offlineStorage'
import { apiEndpoints } from '../config/env'
import { apiService } from './apiService'
import type { TechnicianWorkOrderResponseDto } from '@/types/workOrder'
import type { FileData } from './offlineStorage'
import { preparePhotoForUpload } from '@/utils/preparePhotoForUpload'

export interface WorkOrderState {
  exists: boolean
  hasPendingChanges: boolean
  isSyncing: boolean
  lastModified?: number
  lastSyncTime?: number
  pendingCount: number
  data?: Record<string, unknown>
}

class OfflineWorkOrderService {
  private syncingWorkOrders = new Set<number>();
  private uploadingFiles = new Set<number>(); // Add this line to track files being uploaded

  /**
   * Load work order with offline-first strategy
   * Priority: Local changes > Server > Cache
   */
  async loadWorkOrder(workOrderId: number): Promise<{
    data: TechnicianWorkOrderResponseDto
    state: WorkOrderState
  }> {

    try 
    {
      const offlineWorkOrder = await workOrderOfflineStorage.getWorkOrderOffline(workOrderId)
      const state = await workOrderOfflineStorage.getWorkOrderState(workOrderId)
    
      let failedSyncPendingChanges = false;
      if (state.hasPendingChanges) {
        try {
          await this.syncWorkOrder(workOrderId)
          
          if (offlineWorkOrder) {
            return {
              data: offlineWorkOrder.workOrderResponseData,
              state: {
                ...state,
                isSyncing: this.syncingWorkOrders.has(workOrderId)
              }
            }
          }
                    
        } catch (syncError) {
          failedSyncPendingChanges = true
        }
      }
      
      if (failedSyncPendingChanges) {
        if (offlineWorkOrder) {
          const state = await workOrderOfflineStorage.getWorkOrderState(workOrderId)        
          return {
            data: offlineWorkOrder.workOrderResponseData,
            state: {
              ...state,
              isSyncing: this.syncingWorkOrders.has(workOrderId)
            }
          }
        }
      }
    } catch (error) {
    }  

    try 
    {
      const serverData = await apiService.get<TechnicianWorkOrderResponseDto>(apiEndpoints.workOrders.details(workOrderId))

      await workOrderOfflineStorage.saveWorkOrderOffline(workOrderId, serverData, true)
      
      const updatedState = await workOrderOfflineStorage.getWorkOrderState(workOrderId)
      
      return {
        data: serverData,
        state: {
          ...updatedState,
          isSyncing: this.syncingWorkOrders.has(workOrderId)
        }
      }
    } catch (error) {
    }  

    const offlineWorkOrder = await workOrderOfflineStorage.getWorkOrderOffline(workOrderId)
  
    if (offlineWorkOrder) {
      const state = await workOrderOfflineStorage.getWorkOrderState(workOrderId)        
      return {
        data: offlineWorkOrder.workOrderResponseData,
        state: {
          ...state,
          isSyncing: this.syncingWorkOrders.has(workOrderId)
        }
      }
    }

    throw new Error(`workOrderResponse Work order ${workOrderId} not available offline`)
  }

  /**
   * Save work order changes with queue-first strategy
   * Always queues changes and attempts immediate sync
   */
  async saveWorkOrder(workOrderId: number, changes: Record<string, unknown>, workOrderResponse: TechnicianWorkOrderResponseDto): Promise<void> {
    // 1. Update local state immediately (optimistic update)
    await workOrderOfflineStorage.saveWorkOrderOffline(workOrderId, workOrderResponse, false)
    
    // 2. Remove any existing work order updates from queue before adding new one
    const existingUpdates = await workOrderOfflineStorage.getPendingUpdatesForWorkOrder(workOrderId)
    const existingWorkOrderUpdates = existingUpdates.filter(update => 
      update.type === 'workorder' && update.status !== 'synced'
    )
    
    for (const existingUpdate of existingWorkOrderUpdates) {
      await workOrderOfflineStorage.updatePendingUpdateStatus(existingUpdate.id, 'synced')
    }
    
    // 3. Queue the new change for sync
    const pendingId = await workOrderOfflineStorage.savePendingUpdate(
      workOrderId,
      'workorder',
      'update',
      changes
    )
    
    // 4. Attempt immediate sync
    try {
      await this.syncPendingChange(pendingId)
    } catch (error) {
      // Change stays in queue for later sync
    }
  }

  /**
   * Add alarm with queue-first strategy
   */
  async addAlarm(workOrderId: number, alarmData: Record<string, unknown>, isExisting = false): Promise<void> {

    // Generate temporary ID for offline alarm
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
    const alarmWithTempId = { 
      ...alarmData, 
      tempId, 
      workOrderID_FK: workOrderId,
      isExisting 
    }

    try {
      // 1. Update local state
      const cachedWorkOrder = await workOrderOfflineStorage.getWorkOrderOffline(workOrderId)
      if (cachedWorkOrder) {
        // Create a copy of the data to modify
        const updatedData = {
          ...cachedWorkOrder.workOrderResponseData,
          existingAlarms: [...(cachedWorkOrder.workOrderResponseData.existingAlarms || [])],
          newAlarms: [...(cachedWorkOrder.workOrderResponseData.newAlarms || [])]
        }
        
        if (isExisting) {
          updatedData.existingAlarms.push(alarmWithTempId)
        } else {
          updatedData.newAlarms.push(alarmWithTempId)
        }
        
        // Save the updated complete response
        await workOrderOfflineStorage.saveWorkOrderOffline(workOrderId, updatedData)
      }

      // 2. Queue for sync
      const pendingId = await workOrderOfflineStorage.savePendingUpdate(
        workOrderId,
        'alarm',
        'create',
        alarmWithTempId
      )

      // 3. Attempt immediate sync
      try {
        await this.syncPendingChange(pendingId)
      } catch (error) {
      }

    } catch (error) {
      throw error
    }
  }

  /**
   * Upload photo with queue-first strategy
   */
  async uploadPhoto(workOrderId: number, file: File, alarmId?: number | string, workOrderResponse?: TechnicianWorkOrderResponseDto): Promise<void> {

    try {
      // Resize before persisting so the durable iOS-safe queue and the network
      // upload both use the smaller photo bytes.
      let preparedFile = file
      try {
        preparedFile = await preparePhotoForUpload(file)
      } catch (error) {
        // Resizing should improve storage use, but must not prevent technicians
        // from uploading if a browser cannot decode a particular valid image.
        console.warn('Photo resize failed; queuing the original file', error)
      }

      // 1. Store file locally
      const fileId = await workOrderOfflineStorage.saveFileForUpload(
        preparedFile,
        workOrderId,
        alarmId,
        alarmId ? 'alarm' : 'workorder'
      )

      // 3. Attempt immediate upload
      try {
        await this.syncPendingPhoto(fileId, workOrderResponse)
      } catch (error) {
      }

    } catch (error) {
      throw error
    }
  }

  /**
   * Sync a single pending change
   */
  private async syncPendingChange(pendingId: string): Promise<void> {
    const updates = await workOrderOfflineStorage.getPendingUpdates()
    const update = updates.find(u => u.id === pendingId)
    
    if (!update || update.status === 'synced') {
      return
    }

    await workOrderOfflineStorage.updatePendingUpdateStatus(pendingId, 'syncing')

    try {
      switch (update.type) {
        case 'workorder':
          await workOrderService.updateTechnicianWorkOrder(update.workOrderId, update.data)
          break
        
        case 'alarm':
          if (update.action === 'create') {
            const isExisting = !update.data.isNew
            if (isExisting) {
              await workOrderService.addExistingAlarm(update.workOrderId, update.data)
            } else {
              await workOrderService.addNewAlarm(update.workOrderId, update.data)
            }
          }
          break
        
        case 'note':
          // Handle note updates
          break
        
        default:
      }

      // Mark as synced
      await workOrderOfflineStorage.updatePendingUpdateStatus(pendingId, 'synced')
      
    } catch (error) {
      await workOrderOfflineStorage.updatePendingUpdateStatus(
        pendingId, 
        'failed', 
        error instanceof Error ? error.message : 'Unknown error'
      )
      throw error
    }
  }

  /**
   * Find the real alarm ID by tempId after sync
   */
  private async findRealAlarmIdByTempId(workOrderId: number, offlineId: string, workOrderResponse: TechnicianWorkOrderResponseDto): Promise<number | null> {
    try {
      // First try to find the alarm with the offlineId in cached data (check both arrays)
      let cachedAlarm = null
      if (workOrderResponse.existingAlarms) {
        cachedAlarm = workOrderResponse.existingAlarms.find((alarm) => alarm.offlineId === offlineId)
      }
      if (!cachedAlarm && workOrderResponse.newAlarms) {
        cachedAlarm = workOrderResponse.newAlarms.find((alarm) => alarm.offlineId === offlineId)
      }
      
      if (cachedAlarm && cachedAlarm.workOrderAlarmID) {
        return cachedAlarm.workOrderAlarmID as number
      }

      // If not found locally, call the API to get the alarm ID by temp ID
      try {
        const alarmId = await apiService.get<number>(apiEndpoints.alarms.getByTempId(workOrderId, offlineId))
        return alarmId
      } catch (apiError) {
        console.warn(`Failed to get alarm ID from API for tempId ${offlineId}:`, apiError)
        return null
      }
    } catch (error) {
      console.error(`Error finding real alarm ID for tempId ${offlineId}:`, error)
      return null
    }
  }

  async syncAllPendingPhotos(): Promise<void> {
    const pendingFiles = await workOrderOfflineStorage.getPendingFiles()

    for (const file of pendingFiles) {
      //get work order response
      const workOrderResponse = await workOrderOfflineStorage.getWorkOrderOffline(file.workOrderId)
      if (workOrderResponse) {
        await this.syncPendingPhotoByFile(file, workOrderResponse.workOrderResponseData)
      }
    }
  }

  private async syncPendingPhoto(fileId: number, workOrderResponse?: TechnicianWorkOrderResponseDto): Promise<void> {
    const pendingFiles = await workOrderOfflineStorage.getPendingFiles()
    const fileData = pendingFiles.find(f => f.id === fileId)
    
    if (!fileData) {
      return
    }

    await this.syncPendingPhotoByFile(fileData, workOrderResponse)
  }

  private async syncPendingPhotoByFile(fileData: FileData, workOrderResponse?: TechnicianWorkOrderResponseDto): Promise<void> {
    if (!fileData.id) {
      throw new Error('File data missing ID')
    }

    // Check if this file is already being uploaded
    if (this.uploadingFiles.has(fileData.id)) {
      console.log(`File ${fileData.id} is already being uploaded, skipping`);
      return;
    }

    // Get current file status from database to handle race conditions
    const currentFileData = await workOrderOfflineStorage.getFileById(fileData.id);
    if (!currentFileData) {
      throw new Error(`File ${fileData.id} not found`);
    }

    // Skip if already uploaded or currently uploading
    if (currentFileData.status === 'uploaded') {
      console.log(`File ${fileData.id} already uploaded, skipping`);
      return;
    }

    if (currentFileData.status === 'uploading') {
      console.log(`File ${fileData.id} already uploading, skipping`);
      return;
    }

    // Add to uploading set and update status atomically
    this.uploadingFiles.add(fileData.id);
    
    // Guard against malformed / pre-v3 records that never got real bytes. These
    // can never upload, so drop them instead of looping as 'failed' forever.
    if (!fileData.data || fileData.data.byteLength === 0) {
      console.warn(`File ${fileData.id} has no data, removing unrecoverable record`)
      this.uploadingFiles.delete(fileData.id)
      await workOrderOfflineStorage.deleteFile(fileData.id)
      return
    }

    try {
      await workOrderOfflineStorage.updateFileStatus(fileData.id, 'uploading')

      // Rebuild a File from the stored bytes for upload.
      const file = new File([fileData.data], fileData.name, { type: fileData.fileType })

      if (fileData.type === 'alarm' && fileData.alarmId) {
        // Handle both numeric alarmId and string tempId
        if (typeof fileData.alarmId === 'string') {
          // This is a tempId, need to find the corresponding real alarm ID
          const realAlarmId = await this.findRealAlarmIdByTempId(fileData.workOrderId, fileData.alarmId, workOrderResponse as TechnicianWorkOrderResponseDto)
          if (realAlarmId) {
            await workOrderService.uploadAlarmPhoto(realAlarmId, file)
          } else {
            throw new Error(`Cannot find real alarm ID for offlineId: ${fileData.alarmId}`)
          }
        } else {
          await workOrderService.uploadAlarmPhoto(fileData.alarmId, file)
        }
      } else {
        await workOrderService.uploadWorkOrderPhoto(fileData.workOrderId, file)
      }

      // The server has confirmed the upload, so the durable local copy is no
      // longer needed. Delete it instead of retaining the full photo bytes in
      // IndexedDB indefinitely.
      await workOrderOfflineStorage.deleteFile(fileData.id)
      
    } catch (error) {
      await workOrderOfflineStorage.updateFileStatus(
        fileData.id!, 
        'failed', 
        error instanceof Error ? error.message : 'Unknown error'
      )
      throw error;
    } finally {
      // Always remove from uploading set
      this.uploadingFiles.delete(fileData.id);
    }
  }

  /**
   * Sync all pending changes for a work order (upload-first strategy)
   */
  async syncWorkOrder(workOrderId: number): Promise<boolean> {
    if (this.syncingWorkOrders.has(workOrderId)) {
      return false;
    }

    this.syncingWorkOrders.add(workOrderId)

    try {

      // 1. Upload all pending changes first
      const pendingUpdates = await workOrderOfflineStorage.getPendingUpdatesForWorkOrder(workOrderId)
      
      for (const update of pendingUpdates) {
        if (update.status !== 'synced') {
          await this.syncPendingChange(update.id)
        }
      }

      // 3. Clear pending state
      await workOrderOfflineStorage.clearPendingChanges(workOrderId)

      // // 4. Download fresh data from server
      // try {
      //   const freshData = await apiService.get<TechnicianWorkOrderResponseDto>(apiEndpoints.workOrders.details(workOrderId))
      //   await workOrderOfflineStorage.saveWorkOrderOffline(workOrderId, freshData, true)
      // } catch (error) {
      //   console.warn('Failed to download fresh data after sync:', error)
      // }


      return true;

    } catch (error) {
      throw error
    } finally {
      this.syncingWorkOrders.delete(workOrderId)
    }
  }

  /**
   * Get current state of a work order
   */
  async getWorkOrderState(workOrderId: number): Promise<WorkOrderState> {
    const state = await workOrderOfflineStorage.getWorkOrderState(workOrderId)
    
    return {
      ...state,
      isSyncing: this.syncingWorkOrders.has(workOrderId)
    }
  }

  /**
   * Check if any work orders have pending changes
   */
  async hasPendingChanges(): Promise<boolean> {
    const pendingUpdates = await workOrderOfflineStorage.getPendingUpdates()
    return pendingUpdates.some(u => u.status !== 'synced')
  }

  /**
   * Sync all pending changes across all work orders
   */
  async syncAllPendingChanges(): Promise<void> {
    
    const pendingUpdates = await workOrderOfflineStorage.getPendingUpdates()
    const workOrderIds = [...new Set(pendingUpdates.map(u => u.workOrderId))]

    for (const workOrderId of workOrderIds) {
      try {
        await this.syncWorkOrder(workOrderId)
      } catch (error) {
      }
    }

    await this.syncAllPendingPhotos()
  }

  /**
   * Clear all offline data
   */
  async clearAllData(): Promise<void> {
    await workOrderOfflineStorage.clearDatabase()
  }

  /**
   * Clear specific work order data
   */
  async clearWorkOrderData(workOrderId: number): Promise<void> {
    await workOrderOfflineStorage.clearWorkOrderData(workOrderId)
  }

  /**
   * Clear all pending changes (but keep cached data)
   */
  async clearPendingChanges(): Promise<void> {
    
    const pendingUpdates = await workOrderOfflineStorage.getPendingUpdates()
    
    for (const update of pendingUpdates) {
      await workOrderOfflineStorage.updatePendingUpdateStatus(update.id, 'synced')
    }
    
    // Clear all pending files
    const pendingFiles = await workOrderOfflineStorage.getPendingFiles()
    for (const file of pendingFiles) {
      if (file.id) {
        await workOrderOfflineStorage.deleteFile(file.id)
      }
    }
    
  }
}

export const offlineWorkOrderService = new OfflineWorkOrderService()
